import { ItemPreviewsProvider, MiskatonicTraumaProvider } from '../ItemPresentationContext';
import React, { useEffect, useId, useState } from 'react';
import { ArtworkImage } from '../ArtworkImage';
import { useMonsterTokenStyle } from '../../utils/clientSettings';
import { matchesWikiSearch, wikiSearchText } from './wikiSearch';
import { wikiCategoryArtwork } from './wikiCategoryArtwork';
import { WikiScrollTop } from './WikiScrollTop';
import { getEntityAbilities } from '../../utils/entityAbilities';
import {
  AncientOne,
  Color,
  CultEncounter,
  ExhibitEncounterCard,
  Gate,
  gameApi,
  Investigator,
  InnsmouthLookCard,
  Monster,
  MythosBreakdown as MythosBreakdownType,
  OtherWorldContact,
  UserLanguage,
  WikiDeckType
} from '../../api';
import { Card as ContactCard, getDescription } from '../Card';
import { digitalCardDescription } from '../../utils/digitalCardDescription';
import { SpellCastingPreviewsProvider } from '../SpellCastingContext';
import type { ItemKind, StackedItemWithKind } from '../Card';
import { InvestigatorDisplay } from '../InvestigatorDisplay';
import { WikiMonster } from './WikiMonster';
import { AncientOneDisplay } from '../AncientOneDisplay';
import { GateMarker } from '../GateMarker';
import { MythosBreakdown } from '../MythosBreakdown';
import { Encounter } from './Encounter';
import { ExpansionToggles } from '../ExpansionToggles';
import { HeraldCard } from '../HeraldCard';
import { GuardianCard } from '../GuardianCard';
import { InstitutionDisplay } from '../InstitutionDisplay';
import { SimpleMarkdown } from '../SimpleMarkdown';

interface WikiViewProps {
  onBack: () => void;
  initialDeckType?: WikiDeckType;
  initialLanguage?: UserLanguage;
  initialIncludeBaseCards?: boolean;
  initialIncludeDunwichCards?: boolean;
  initialIncludeDarkPharaohCards?: boolean;
  initialIncludeKingsportCards?: boolean;
  initialIncludeKingInYellowCards?: boolean;
  initialIncludeBlackGoatCards?: boolean;
  initialIncludeMiskatonicCards?: boolean;
  initialIncludeLurkerCards?: boolean;
  initialIncludeInsmouthCards?: boolean;
  initialIncludeFanMadeInvestigators?: boolean;
}

interface DeckTab {
  id: WikiDeckType;
  label: string;
}

interface GroupedCardEntry<T> {
  key: string;
  card: T;
  count: number;
}

const DECK_TABS: DeckTab[] = [
  { id: 'CommonItems', label: 'Common Items' },
  { id: 'UniqueItems', label: 'Unique Items' },
  { id: 'Exhibits', label: 'Exhibits' },
  { id: 'Spells', label: 'Spells' },
  { id: 'Skills', label: 'Skills' },
  { id: 'Allies', label: 'Allies' },
  { id: 'Monsters', label: 'Monsters' },
  { id: 'AncientOnes', label: 'Ancient Ones' },
  { id: 'Heralds', label: 'Heralds' },
  { id: 'Guardians', label: 'Guardians' },
  { id: 'Institutions', label: 'Institutions' },
  { id: 'Investigator', label: 'Investigators' },
  { id: 'Special', label: 'Special' },
  { id: 'Injuries', label: 'Injuries' },
  { id: 'Madness', label: 'Madnesses' },
  { id: 'Corruptions', label: 'Corruptions' },
  { id: 'Relationships', label: 'Relationships' },
  { id: 'Gates', label: 'Gates' },
  { id: 'Mythos', label: 'Mythos' },
  { id: 'Contact', label: 'Encounters' },
  { id: 'CultEncounters', label: 'Cult Encounters' },
  { id: 'InnsmouthLook', label: 'Innsmouth Look' },
  { id: 'OtherWorld', label: 'Other Worlds' },
];

const GROUPED_DECK_TYPES: Set<string> = new Set([
  'CommonItems',
  'UniqueItems',
  'Exhibits',
  'Spells',
  'Skills',
  'Monsters',
  'Injuries',
  'Madness',
  'Gates',
]);

const shouldGroupDeckByName = (deckType: WikiDeckType) => GROUPED_DECK_TYPES.has(deckType);

const groupCardsByName = <T extends { name?: string | null }>(cards: T[]): GroupedCardEntry<T>[] => {
  const groupedCards = new Map<string, GroupedCardEntry<T>>();

  cards.forEach((card, index) => {
    if (!card) {
      return;
    }

    const normalizedName = typeof card.name === 'string' ? card.name.trim() : '';
    const key = normalizedName.length > 0 ? normalizedName : `__unnamed_${index}`;
    const existingCard = groupedCards.get(key);

    if (existingCard) {
      existingCard.count += 1;
      return;
    }

    groupedCards.set(key, {
      key,
      card,
      count: 1,
    });
  });

  return Array.from(groupedCards.values());
};

const getKindByDeckType = (
  deckType: WikiDeckType
): ItemKind | 'contact' | 'cultencounters' | 'innsmouthlook' | 'investigator' | 'otherworld' | 'monster' | 'ancientones' | 'heralds' | 'guardians' | 'institutions' | 'gates' | 'mythos' => {
  switch (deckType) {
    case 'CommonItems':
      return 'common';
    case 'UniqueItems':
      return 'unique';
    case 'Exhibits':
      return 'unique';
    case 'Spells':
      return 'spell';
    case 'Skills':
      return 'skill';
    case 'Allies':
      return 'ally';
    case 'Special':
      return 'special';
    case 'Injuries':
      return 'injury';
    case 'Madness':
      return 'madness';
    case 'Corruptions':
      return 'corruption';
    case 'Relationships':
      return 'relationship';
    case 'Gates':
      return 'gates';
    case 'Mythos':
      return 'mythos';
    case 'Contact':
      return 'contact';
    case 'CultEncounters':
      return 'cultencounters';
    case 'InnsmouthLook':
      return 'innsmouthlook';
    case 'Investigator':
      return 'investigator';
    case 'OtherWorld':
      return 'otherworld';
    case 'Monsters':
      return 'monster';
    case 'AncientOnes':
      return 'ancientones';
    case 'Heralds':
      return 'heralds';
    case 'Institutions':
      return 'institutions';
    case 'Guardians':
      return 'guardians';
    default:
      return 'common';
  }
};

export const WikiView: React.FC<WikiViewProps> = ({
  onBack,
  initialDeckType,
  initialLanguage,
  initialIncludeBaseCards,
  initialIncludeDunwichCards,
  initialIncludeDarkPharaohCards,
  initialIncludeKingsportCards,
  initialIncludeKingInYellowCards,
  initialIncludeBlackGoatCards,
  initialIncludeMiskatonicCards,
  initialIncludeLurkerCards,
  initialIncludeInsmouthCards,
  initialIncludeFanMadeInvestigators
}) => {
  const [search, setSearch] = useState('');
  const artworkStyle = useMonsterTokenStyle();
  const searchId = useId();
  const [language, setLanguage] = useState<UserLanguage>(initialLanguage ?? 'En');
  const [deckType, setDeckType] = useState<WikiDeckType>(initialDeckType ?? 'CommonItems');
  const [includeBaseCards, setIncludeBaseCards] = useState<boolean>(initialIncludeBaseCards ?? true);
  const [includeDunwichCards, setIncludeDunwichCards] = useState<boolean>(initialIncludeDunwichCards ?? true);
  const [includeDarkPharaohCards, setIncludeDarkPharaohCards] = useState<boolean>(
    initialIncludeDarkPharaohCards ?? true
  );
  const [includeKingsportCards, setIncludeKingsportCards] = useState<boolean>(
    initialIncludeKingsportCards ?? true
  );
  const [includeKingInYellowCards, setIncludeKingInYellowCards] = useState<boolean>(
    initialIncludeKingInYellowCards ?? true
  );
  const [includeBlackGoatCards, setIncludeBlackGoatCards] = useState<boolean>(
    initialIncludeBlackGoatCards ?? true
  );
  const [includeMiskatonicCards, setIncludeMiskatonicCards] = useState<boolean>(initialIncludeMiskatonicCards ?? true);
  const [includeLurkerCards, setIncludeLurkerCards] = useState<boolean>(initialIncludeLurkerCards ?? true);
  const [includeInsmouthCards, setIncludeInsmouthCards] = useState<boolean>(
    initialIncludeInsmouthCards ?? true
  );
  const [includeFanMadeInvestigators, setIncludeFanMadeInvestigators] = useState<boolean>(
    initialIncludeFanMadeInvestigators ?? true
  );
  const [cards, setCards] = useState<any[]>([]);
  const [exhibitEncounterCards, setExhibitEncounterCards] = useState<ExhibitEncounterCard[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDeckType(initialDeckType ?? 'CommonItems');
  }, [initialDeckType]);

  useEffect(() => {
    setLanguage(initialLanguage ?? 'En');
  }, [initialLanguage]);

  useEffect(() => {
    setIncludeBaseCards(initialIncludeBaseCards ?? true);
  }, [initialIncludeBaseCards]);

  useEffect(() => {
    setIncludeDunwichCards(initialIncludeDunwichCards ?? true);
  }, [initialIncludeDunwichCards]);

  useEffect(() => {
    setIncludeDarkPharaohCards(initialIncludeDarkPharaohCards ?? true);
  }, [initialIncludeDarkPharaohCards]);

  useEffect(() => {
    setIncludeKingsportCards(initialIncludeKingsportCards ?? true);
  }, [initialIncludeKingsportCards]);

  useEffect(() => {
    setIncludeKingInYellowCards(initialIncludeKingInYellowCards ?? true);
  }, [initialIncludeKingInYellowCards]);

  useEffect(() => {
    setIncludeBlackGoatCards(initialIncludeBlackGoatCards ?? true);
  }, [initialIncludeBlackGoatCards]);

  useEffect(() => {
    setIncludeMiskatonicCards(initialIncludeMiskatonicCards ?? true);
  }, [initialIncludeMiskatonicCards]);

  useEffect(() => {
    setIncludeLurkerCards(initialIncludeLurkerCards ?? true);
  }, [initialIncludeLurkerCards]);

  useEffect(() => {
    setIncludeInsmouthCards(initialIncludeInsmouthCards ?? true);
  }, [initialIncludeInsmouthCards]);

  useEffect(() => {
    setIncludeFanMadeInvestigators(initialIncludeFanMadeInvestigators ?? true);
  }, [initialIncludeFanMadeInvestigators]);

  useEffect(() => {
    let cancelled = false;

    if (!includeDunwichCards && (deckType === 'Madness' || deckType === 'Injuries')) {
      setDeckType('CommonItems');
    }
    if (!includeDarkPharaohCards && deckType === 'Exhibits') {
      setDeckType('CommonItems');
    }
    if (!includeKingsportCards && deckType === 'Guardians') {
      setDeckType('CommonItems');
    }
    if (!includeDarkPharaohCards && !includeKingsportCards && deckType === 'Heralds') {
      setDeckType('CommonItems');
    }
    if (!includeBlackGoatCards && (deckType === 'Corruptions' || deckType === 'CultEncounters')) {
      setDeckType('CommonItems');
    }
    if (!includeInsmouthCards && deckType === 'InnsmouthLook') {
      setDeckType('CommonItems');
    }
    if (!includeMiskatonicCards && deckType === 'Institutions') {
      setDeckType('CommonItems');
    }
    if (!includeLurkerCards && deckType === 'Relationships') {
      setDeckType('CommonItems');
    }

    const loadDeck = async () => {
      setLoading(true);
      setError(null);

      try {
        const baseArguments = [
          deckType,
          language,
          includeBaseCards,
          includeDunwichCards,
          includeDarkPharaohCards,
          includeKingsportCards,
        ] as const;
        const payload = await gameApi.getWikiDeck(
          ...baseArguments,
          includeFanMadeInvestigators,
          includeKingInYellowCards,
          includeBlackGoatCards,
          includeInsmouthCards,
          includeLurkerCards,
          includeMiskatonicCards
        );
        if (!cancelled) {
          setCards(payload.cards ?? []);
          setExhibitEncounterCards(payload.deckType === 'Exhibits' ? payload.encounterCards ?? [] : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load cards');
          setCards([]);
          setExhibitEncounterCards([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDeck();

    return () => {
      cancelled = true;
    };
  }, [deckType, language, includeBaseCards, includeDunwichCards, includeDarkPharaohCards, includeKingsportCards, includeKingInYellowCards, includeBlackGoatCards, includeInsmouthCards, includeLurkerCards, includeMiskatonicCards, includeFanMadeInvestigators]);

  const cardKind = getKindByDeckType(deckType);
  const itemKinds = new Set(['common', 'unique', 'spell', 'skill', 'ally', 'special', 'injury', 'madness', 'corruption', 'relationship']);
  const visibleCards = cards.filter(card => {
    let text = wikiSearchText(card);
    if (artworkStyle === 'Digital' && (deckType === 'Heralds' || deckType === 'Guardians')) {
      text += '\n' + (getEntityAbilities(deckType === 'Heralds' ? 'Herald' : 'Guardian', card?.name) ?? [])
        .map(ability => `${ability.title}\n${ability.text}`).join('\n');
    }
    if (card && itemKinds.has(cardKind)) {
      const description = getDescription({ item: card, kind: cardKind as ItemKind });
      text += '\n' + (artworkStyle === 'Digital' ? digitalCardDescription(cardKind, card.name, description) : description);
    }
    return matchesWikiSearch(text, search);
  });
  const visibleExhibitEncounters = exhibitEncounterCards.filter(card => matchesWikiSearch(wikiSearchText(card), search));
  const matchingNames = new Set(visibleCards.map(card => card?.name).filter(Boolean));
  const groupedCards = shouldGroupDeckByName(deckType)
    ? groupCardsByName(cards).filter(entry => entry.card?.name ? matchingNames.has(entry.card.name) : visibleCards.includes(entry.card))
    : visibleCards.map((card, index) => ({
      key: `__ungrouped_${index}`,
      card,
      count: 1,
    }));

  return (
    <MiskatonicTraumaProvider enabled={includeMiskatonicCards}><SpellCastingPreviewsProvider previews={{}}><ItemPreviewsProvider previews={{}}><div className="wiki-view">
      <div className="wiki-header">
        <h2>Wiki</h2>
        <button onClick={onBack}>Back</button>
      </div>

      <div className="wiki-controls">
        {/* <label className="wiki-language">
          Language
          <select
            className="select-input"
            value={language}
            onChange={(event) => setLanguage(event.target.value as UserLanguage)}
          >
            <option value="En">En</option>
            <option value="Ru">Ru</option>
          </select>
        </label> */}
        <ExpansionToggles
          includeBaseCards={includeBaseCards}
          includeDunwichCards={includeDunwichCards}
          includeDarkPharaohCards={includeDarkPharaohCards}
          includeKingsportCards={includeKingsportCards}
          includeKingInYellowCards={includeKingInYellowCards}
          includeBlackGoatCards={includeBlackGoatCards}
          includeMiskatonicCards={includeMiskatonicCards}
          includeLurkerCards={includeLurkerCards}
          includeInsmouthCards={includeInsmouthCards}
          includeFanMadeInvestigators={includeFanMadeInvestigators}
          showAdminOnly={true}
          onIncludeBaseCardsChange={setIncludeBaseCards}
          onIncludeDunwichCardsChange={setIncludeDunwichCards}
          onIncludeDarkPharaohCardsChange={setIncludeDarkPharaohCards}
          onIncludeKingsportCardsChange={setIncludeKingsportCards}
          onIncludeKingInYellowCardsChange={setIncludeKingInYellowCards}
          onIncludeBlackGoatCardsChange={setIncludeBlackGoatCards}
          onIncludeMiskatonicCardsChange={setIncludeMiskatonicCards}
          onIncludeLurkerCardsChange={setIncludeLurkerCards}
          onIncludeInsmouthCardsChange={setIncludeInsmouthCards}
          onIncludeFanMadeInvestigatorsChange={setIncludeFanMadeInvestigators}
        />

        <div className="wiki-search">
          <label htmlFor={searchId}>Search cards</label>
          <div className="wiki-search-input">
            <input id={searchId} type="search" value={search} placeholder="Name or description" onChange={event => setSearch(event.target.value)} />
            {search && <button type="button" onClick={() => setSearch('')}>Clear search</button>}
          </div>
        </div>
        <div className="wiki-tabs" role="tablist" aria-label="Card types">
          {DECK_TABS.map((tab) => {
            if (!includeDunwichCards && (tab.id === 'Madness' || tab.id === 'Injuries')) {
              return null;
            }
            if (!includeDarkPharaohCards && tab.id === 'Exhibits') {
              return null;
            }
            if (!includeKingsportCards && tab.id === 'Guardians') {
              return null;
            }
            if (!includeDarkPharaohCards && !includeKingsportCards && tab.id === 'Heralds') {
              return null;
            }
            if (!includeBlackGoatCards && (tab.id === 'Corruptions' || tab.id === 'CultEncounters')) {
              return null;
            }
            if (!includeInsmouthCards && tab.id === 'InnsmouthLook') {
              return null;
            }
            if (!includeMiskatonicCards && tab.id === 'Institutions') return null;
            if (!includeLurkerCards && tab.id === 'Relationships') return null;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={deckType === tab.id}
                className={`wiki-tab ${deckType === tab.id ? 'active' : ''}`}
                onClick={() => setDeckType(tab.id)}
              >
                <ArtworkImage className="wiki-category-artwork" src={wikiCategoryArtwork(tab.id, artworkStyle)} alt="" aria-hidden="true" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {loading && <div>Loading cards...</div>}
      {error && <div className="error">{error}</div>}
      {!loading && !error && deckType !== 'Contact' && deckType !== 'OtherWorld' && visibleCards.length === 0 && visibleExhibitEncounters.length === 0 && <p role="status">No cards match your search</p>}

      {!loading && !error && deckType === 'Contact' && (
        <Encounters cards={cards} search={search} />
      )}

      {!loading && !error && deckType === 'CultEncounters' && (
        <WikiCultEncounters encounters={visibleCards as CultEncounter[]} />
      )}

      {!loading && !error && deckType === 'InnsmouthLook' && (
        <WikiInnsmouthLook cards={visibleCards as InnsmouthLookCard[]} />
      )}

      {!loading && !error && deckType === 'Investigator' && (
        <Investigators cards={visibleCards} deckCards={cards} />
      )}

      {!loading && !error && deckType === 'OtherWorld' && (
        <OtherWorldContacts cards={cards} search={search} />
      )}

      {!loading && !error && deckType === 'Monsters' && (
        <WikiMonsters monsters={visibleCards} />
      )}

      {!loading && !error && deckType === 'AncientOnes' && (
        <WikiAncientOnes ancientOnes={visibleCards} />
      )}

      {!loading && !error && deckType === 'Heralds' && (
        <div className="wiki-cards-grid">
          {visibleCards.map((herald, index) => (
            <HeraldCard key={`wiki-herald-${herald.name ?? index}`} herald={herald} />
          ))}
        </div>
      )}

      {!loading && !error && deckType === 'Institutions' && (
        <div className="wiki-cards-grid">
          {visibleCards.map(institution => (
            <InstitutionDisplay key={institution.id} institution={institution} referenceOnly />
          ))}
        </div>
      )}

      {!loading && !error && deckType === 'Guardians' && (
        <div className="wiki-cards-grid">
          {visibleCards.map((guardian, index) => (
            <GuardianCard key={`wiki-guardian-${guardian.name ?? index}`} guardian={guardian} />
          ))}
        </div>
      )}

      {!loading && !error && deckType === 'Gates' && (
        <WikiGates gates={visibleCards} language={language} />
      )}

      {!loading && !error && deckType === 'Mythos' && (
        <div className="wiki-cards-grid">
          {(visibleCards as MythosBreakdownType[]).map((mythosDetails, index) => (
            <div className='wiki-mythos' key={`wiki-mythos-${index}`}>
              <MythosBreakdown mythosDetails={mythosDetails} />
            </div>
          ))}
        </div>
      )}

      {!loading && !error && deckType !== 'Contact' && deckType !== 'CultEncounters' && deckType !== 'InnsmouthLook' && deckType !== 'Investigator' && deckType !== 'OtherWorld' && deckType !== 'Monsters' && deckType !== 'AncientOnes' && deckType !== 'Heralds' && deckType !== 'Guardians' && deckType !== 'Institutions' && deckType !== 'Gates' && deckType !== 'Mythos' && (
        <div>
          <div className="wiki-cards-grid">
            {groupedCards.map((entry) => {
              if (cardKind === 'contact' || cardKind === 'cultencounters' || cardKind === 'innsmouthlook' || cardKind === 'investigator' || cardKind === 'otherworld' || cardKind === 'monster' || cardKind === 'ancientones' || cardKind === 'heralds' || cardKind === 'guardians' || cardKind === 'institutions' || cardKind === 'gates' || cardKind === 'mythos') {
                return null;
              }

              const cardEntry: StackedItemWithKind = {
                item: entry.card,
                kind: cardKind,
              };

              return (
                <div key={`${deckType}-${entry.key}`} style={{
                  position: 'relative'
                }}>
                  <ContactCard
                    card={cardEntry}
                  />
                  {entry.count > 1 && <div className='wiki-card-count'>x{entry.count}</div>}
                </div>
              );
            })}
          </div>
          {deckType === 'Exhibits' && visibleExhibitEncounters.length > 0 && (
            <div>
              <h3>Exhibit Encounters <small>({visibleExhibitEncounters.length})</small></h3>
              <div className='contacts'>
                {visibleExhibitEncounters.map((encounter, index) => (
                  <div key={`exhibit-encounter-${encounter.name}-${index}`}>
                    <h5>{encounter.name}</h5>
                    <Encounter
                      location="Exhibit Encounters"
                      text={encounter.description}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <WikiScrollTop />
    </div></ItemPreviewsProvider></SpellCastingPreviewsProvider></MiskatonicTraumaProvider>
  );
};

type WikiAncientOnesType = {
  ancientOnes: AncientOne[]
}

const WikiAncientOnes = ({ ancientOnes }: WikiAncientOnesType) => {
  return (
    <div className="wiki-cards-grid wiki-ancient-ones-grid">
      {ancientOnes.map((ancientOne) => (
        <AncientOneDisplay
          key={ancientOne.name}
          ancientOne={ancientOne}
          responsive
          worshippersDescription={ancientOne.worshippersDescription}
          powerDescription={ancientOne.powerDescription}
          attackDescription={ancientOne.attackDescription}
        />
      ))}
    </div>
  );
}

type WikiMonstersType = {
  monsters: Monster[]
}
const WikiMonsters = ({ monsters }: WikiMonstersType) => {
  const monsterCards = monsters.filter(
    (monster): monster is Monster => !!monster && typeof monster.name === 'string' && monster.name.length > 0
  );
  const groupedMonsters = groupCardsByName(monsterCards);

  return (
    <div className="wiki-cards-grid wiki-monsters-grid">
      {groupedMonsters.map(({ card: monster, key, count }) => (
        <div key={key} className="wiki-monster-card">
          <WikiMonster monster={monster} />
          <div className="wiki-monster-name">
            <b>{monster.name}</b>
            <small>{count > 1 ? ` x${count}` : ''}</small>
          </div>
        </div>
      ))}
    </div>
  );
}

type WikiGatesType = {
  gates: Gate[];
  language: string;
}

const groupGates = (gates: Gate[]): GroupedCardEntry<Gate>[] => {
  const groupedGates = new Map<string, GroupedCardEntry<Gate>>();

  gates.forEach((gate, index) => {
    if (!gate) {
      return;
    }

    const key = `${gate.otherWorld}|${gate.dimensionSign}|${gate.modifier}`;
    const existingGate = groupedGates.get(key);

    if (existingGate) {
      existingGate.count += 1;
      return;
    }

    groupedGates.set(key, {
      key: key || `__unnamed_gate_${index}`,
      card: gate,
      count: 1,
    });
  });

  return Array.from(groupedGates.values());
};

const WikiGates = ({ gates, language }: WikiGatesType) => {
  const groupedGates = groupGates(gates);

  return (
    <div className="wiki-cards-grid wiki-gates-grid">
      {groupedGates.map(({ card: gate, key, count }) => (
        <div key={key} className="wiki-gate-card" style={{ position: 'relative' }}>
          <GateMarker gate={gate} language={language} />
          {count > 1 && <div className='wiki-card-count'>x{count}</div>}
        </div>
      ))}
    </div>
  );
}

const MOBILE_WIKI_QUERY = '(max-width: 767px), (max-width: 1024px) and (max-height: 500px)';
const Investigators = ({ cards, deckCards }: { cards: Investigator[]; deckCards: Investigator[] }) => {
  const style = useMonsterTokenStyle();
  const [mobile, setMobile] = useState(() => window.matchMedia?.(MOBILE_WIKI_QUERY).matches ?? false);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const id = useId();
  useEffect(() => {
    const media = window.matchMedia?.(MOBILE_WIKI_QUERY);
    if (!media) return;
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  useEffect(() => setExpanded(new Set()), [deckCards]);
  const compact = mobile && style === 'Digital';
  const toggle = (name: string) => setExpanded(previous => {
    const next = new Set(previous);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });
  return <div className="wiki-cards-grid">
    {cards.map(investigator => {
      const open = expanded.has(investigator.name);
      const contentId = id + '-' + deckCards.indexOf(investigator);
      return <section className="wiki-investigator" key={investigator.name}>
        {compact && <button type="button" className="wiki-investigator-toggle" aria-expanded={open} aria-controls={contentId}
          onClick={() => toggle(investigator.name)}>{open ? 'Collapse' : 'Expand'} {investigator.name}</button>}
        <div id={contentId} onClick={compact && !open ? event => {
          if (!(event.target as Element).closest('button, a, input, summary, [role="button"]')) toggle(investigator.name);
        } : undefined} className={compact && !open ? 'wiki-investigator-preview' : undefined}>
          <InvestigatorDisplay variant="reference" selectionPreview={compact && !open} investigator={investigator} />
        </div>
      </section>;
    })}
  </div>;
};

type EncounterLocationCard = {
  locationName: string;
  name: string;
}

type WikiCultEncountersProps = {
  encounters: CultEncounter[];
}

const WikiCultEncounters = ({ encounters }: WikiCultEncountersProps) => {
  const groupedEncounters = groupCardsByName(encounters);

  return (
    <div className="contacts">
      {groupedEncounters.map(({ card: encounter, key, count }) => (
        <div key={key} className="wiki-cult-encounter">
          <h5>
            {encounter.name}
            {count > 1 && <small> x{count}</small>}
          </h5>
          <Encounter location="Cult Encounters" text={encounter.description} />
        </div>
      ))}
    </div>
  );
}

type WikiInnsmouthLookProps = {
  cards: InnsmouthLookCard[];
}

const WikiInnsmouthLook = ({ cards }: WikiInnsmouthLookProps) => {
  const grouped = new Map<string, { card: InnsmouthLookCard; count: number }>();
  cards.forEach((card, index) => {
    const key = card.textDescription?.trim() || `__innsmouth_look_${index}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      grouped.set(key, { card, count: 1 });
    }
  });

  return (
    <div className="contacts">
      {Array.from(grouped.entries()).map(([key, entry], index) => (
        <div key={key} className="wiki-innsmouth-look-card">
          <SimpleMarkdown
            text={entry.card.textDescription}
            keyPrefix={`innsmouth-look-${index}`}
          />
          {entry.count > 1 && <small> x{entry.count}</small>}
        </div>
      ))}
    </div>
  );
}

type EncounterEntryType = {
  cards: EncounterLocationCard[]
}
const Encounters = ({cards, search}: EncounterEntryType & { search: string }) => {
  const cardsByLocations = cards.reduce((acc: Record<string, EncounterLocationCard[]>, card: EncounterLocationCard) => {
    if (!acc[card.locationName]) {
      acc[card.locationName] = [card];
    } else {
      acc[card.locationName].push(card);
    }
    return acc;
  }, {});
  const sortedLocations = Object.keys(cardsByLocations).sort((a, b) => a.localeCompare(b));
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  return (
    <div>
      <div className='wiki-tabs'>
        {sortedLocations.map((location) => {
          return (
            <button
              key={location}
              className={`wiki-tab small ${selectedLocations.includes(location) ? 'active' : ''}`}
              onClick={() => selectedLocations.includes(location) ? setSelectedLocations(selectedLocations.filter((l) => l !== location)) : setSelectedLocations([...selectedLocations, location])}
            >
              {location}
            </button>
          );
        })}
      </div>
      {!sortedLocations.some(location => (selectedLocations.length === 0 || selectedLocations.includes(location)) && cardsByLocations[location].some(card => matchesWikiSearch(card.name, search))) && <p role="status">No cards match your search</p>}
      {sortedLocations.map((location) => {
        const locationCards = cardsByLocations[location].filter(card => matchesWikiSearch(card.name, search));
        if (locationCards.length > 0 && (selectedLocations.length === 0 || selectedLocations.includes(location))) {
          return (
            <div key={location}>
              <h3>{location} <small>({locationCards.length})</small></h3>
              <div className='contacts'>
                {locationCards.map((card, index) => <Encounter key={`${location}-${card.name}-${index}`} text={card.name} location={location} />)}
              </div>
            </div>
          )
        } else {
          return null;
        }
    })}
    </div>
  );
}

const OW = [
    'AnotherDimension',
    'Abyss',
    'CityOfTheGreatRace',
    'GreatHallOfCeleano',
    'Yuggoth',
    'PlateauOfLeng',
    'Rlyeh',
    'Dreamlands',
    'LostCarcosa',
    'AnotherTime'
];

const otherWorldDisplayTextById: Record<string, string> = {
  AnotherDimension: 'Another Dimension',
  Abyss: 'Abyss',
  CityOfTheGreatRace: 'City Of The Great Race',
  GreatHallOfCeleano: 'Great Hall Of Celeano',
  Yuggoth: 'Yuggoth',
  PlateauOfLeng: 'Plateau Of Leng',
  Rlyeh: 'Rlyeh',
  Dreamlands: 'Dreamlands',
  LostCarcosa: 'Lost Carcosa',
  AnotherTime: 'Another Time',
};

const colorsByOw: Record<string, Color[]> = {
  AnotherDimension: ['Green', 'Blue', 'Yellow', 'Red'],
  Abyss: ['Blue', 'Red'],
  CityOfTheGreatRace: ['Green', 'Yellow'],
  GreatHallOfCeleano: ['Green', 'Blue'],
  Yuggoth: ['Yellow', 'Blue'],
  PlateauOfLeng: ['Green', 'Red'],
  Rlyeh: ['Yellow', 'Red'],
  Dreamlands: ['Green', 'Blue', 'Yellow', 'Red'],
  LostCarcosa: ['Blue', 'Yellow'],
  AnotherTime: ['Red', 'Green'],
};

type OtherWorldContactType = {
  cards: OtherWorldContact[]
}
const OtherWorldContacts = ({cards, search}: OtherWorldContactType & { search: string }) => {
  const sortedOw = [...OW].sort((a, b) =>
    (otherWorldDisplayTextById[a] ?? a).localeCompare(otherWorldDisplayTextById[b] ?? b)
  );
  const cardsByWorlds = cards.reduce((acc: Record<string, string[]>, card: OtherWorldContact) => {
    if (!card || !card.resolvers) {
      return acc;
    }

    for (let i = 0; i < sortedOw.length; i++) {
      const ow = sortedOw[i];

      if (!colorsByOw[ow].includes(card.color)) {
        continue;
      }

      const otherWorlds = Object.keys(card.resolvers);

      const description = otherWorlds.includes(ow) ? card.resolvers[ow] : card.resolvers.Other;
      if (!description) continue;

      if (!acc[ow]) {
        acc[ow] = [description.description];
      } else {
        acc[ow].push(description.description);
      }
    }
    return acc;
  }, {});
  const [selectedOw, setSelectedOw] = useState<string[]>([]);

  return (
    <div>
      <div className='wiki-tabs'>
        {sortedOw.map((location) => {
          return (
            <button
              key={location}
              className={`wiki-tab small ${selectedOw.includes(location) ? 'active' : ''}`}
              onClick={() => selectedOw.includes(location) ? setSelectedOw(selectedOw.filter((l) => l !== location)) : setSelectedOw([...selectedOw, location])}
            >
              {otherWorldDisplayTextById[location] ?? location}
            </button>
          );
        })}
      </div>
      {!sortedOw.some(world => (selectedOw.length === 0 || selectedOw.includes(world)) && cardsByWorlds[world]?.some(text => matchesWikiSearch(text, search))) && <p role="status">No cards match your search</p>}
      {sortedOw.map((otherWorldId) => {
        const worldCards = cardsByWorlds[otherWorldId]?.filter(text => matchesWikiSearch(text, search));

        if (!worldCards?.length) {
          return null;
        }

        if (selectedOw.length === 0 || selectedOw.includes(otherWorldId)) {
          return (
            <div key={otherWorldId}>
              <h3>{otherWorldDisplayTextById[otherWorldId] ?? otherWorldId}</h3>
              <div className='contacts'>
                {worldCards.map((card, index) => <Encounter key={`${otherWorldId}-${index}`} location={otherWorldId} text={card} />)}
              </div>
            </div>
          )
        } else {
          return null;
        }
    })}
    </div>
  );
}
